// src/pages/admin/RoleMaster.tsx - UPDATED WITH EDIT/DELETE ACTIONS
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useAddRoleMutation,
  useListRolesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  Role,
} from '../../features/admin/roleMasterApi.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const RoleMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({ role_name: '', role_desc: '' });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    const adminType = localStorage.getItem('admin_type') || localStorage.getItem('user_role') || 'operation';
    const cleanRole = adminType.toString().toLowerCase().trim();
    const isSuper =
      cleanRole.includes('super') ||
      cleanRole === 'super_admin' ||
      cleanRole === 'super admin' ||
      cleanRole === 'superadmin' ||
      cleanRole === 'super';
    setIsSuperAdmin(isSuper);

    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) setApiError('Authentication token not found. Please login again.');
  }, []);

  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    refetch: refetchRoles,
    error: rolesError,
    isError: isRolesError,
  } = useListRolesQuery(undefined, { skip: !isSuperAdmin });

  const [addRole, { isLoading: isAdding }] = useAddRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  useEffect(() => {
    if (rolesData?.error) setApiError(rolesData.message || 'API Error');
    else if (isRolesError) setApiError('Failed to connect to server.');
    else setApiError('');
  }, [rolesData, isRolesError]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditMode(true);
      setSelectedRole(role);
      setFormData({ role_name: role.role_name, role_desc: role.role_desc || '' });
    } else {
      setEditMode(false);
      setSelectedRole(null);
      setFormData({ role_name: '', role_desc: '' });
    }
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedRole(null);
    setFormData({ role_name: '', role_desc: '' });
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.role_name.trim()) newErrors.role_name = 'Role name is required';
    if (!formData.role_desc.trim()) newErrors.role_desc = 'Role description is required';
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (editMode && selectedRole) {
        // ✅ Send role_id as required by backend
        const result = await updateRole({
          role_id: selectedRole.id,
          role_name: formData.role_name.trim(),
          role_desc: formData.role_desc.trim(),
        }).unwrap();

        if (!result.error) {
          setSnackbar({ open: true, message: result.message || 'Role updated successfully', severity: 'success' });
          refetchRoles();
          setTimeout(handleCloseDialog, 800);
        } else {
          setSnackbar({ open: true, message: result.message || 'Failed to update role', severity: 'error' });
        }
      } else {
        const result = await addRole({
          role_name: formData.role_name.trim(),
          role_desc: formData.role_desc.trim(),
        }).unwrap();

        if (!result.error) {
          setSnackbar({ open: true, message: result.message || 'Role added successfully', severity: 'success' });
          refetchRoles();
          setTimeout(handleCloseDialog, 800);
        } else {
          setSnackbar({ open: true, message: result.message || 'Failed to add role', severity: 'error' });
        }
      }
    } catch (error: any) {
      const msg = error?.data?.message || error?.error || 'Operation failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleDelete = async (role: Role) => {
    if (!window.confirm(`Are you sure you want to delete role "${role.role_name}"?`)) return;
    try {
      // ✅ Send role_id as required by backend
      const result = await deleteRole(role.id).unwrap();
      if (!result.error) {
        setSnackbar({ open: true, message: result.message || 'Role deleted successfully', severity: 'success' });
        refetchRoles();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to delete role', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.data?.message || 'Failed to delete role', severity: 'error' });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString.replace(' ', 'T')).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return dateString; }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <AdminHeader onToggleMenu={handleDrawerToggle} />
          </Box>
        </Box>
      </Box>
    );
  }

  const displayRoles = rolesData?.roles || [];
  const loading = isLoadingRoles;
  const isSaving = isAdding || isUpdating;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Mobile Drawer */}
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SecurityIcon sx={{ fontSize: 28, color: '#008060' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Role Master</Typography>
                  <Chip label="Super Admin Only" size="small" color="warning" sx={{ fontWeight: 600 }} />
                  {apiError && <Chip label="API Error" size="small" color="error" sx={{ fontWeight: 600 }} />}
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={loading}
                  sx={{ textTransform: 'none', bgcolor: '#008060', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' }, '&:disabled': { bgcolor: '#e1e3e5', color: '#6d7175' } }}>
                  Add Role
                </Button>
              </Box>

              {apiError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">API Connection Issue</Typography>
                  <Typography variant="body2">{apiError}</Typography>
                </Alert>
              )}

              {isRolesError && !apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Failed to load roles</Typography>
                  <Typography variant="body2">{(rolesError as any)?.data?.message || 'Please check your connection.'}</Typography>
                </Alert>
              )}

              {/* Table */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                    <Typography sx={{ ml: 2 }}>Loading roles...</Typography>
                  </Box>
                ) : displayRoles.length > 0 ? (
                  <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                    {/* ✅ Added Actions column */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 2fr 160px 100px',
                      gap: 2, px: 3, py: 1.5,
                      borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', minWidth: '700px',
                    }}>
                      {['S.No', 'Role Name', 'Description', 'Created Date', 'Actions'].map((col, i) => (
                        <Typography key={col} variant="caption"
                          sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: i === 4 ? 'right' : 'left' }}>
                          {col}
                        </Typography>
                      ))}
                    </Box>

                    {displayRoles.map((role, index) => (
                      <Box key={role.id} sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 2fr 160px 100px',
                        gap: 2, px: 3, py: 2, alignItems: 'center',
                        borderBottom: '1px solid #e1e3e5',
                        '&:hover': { bgcolor: '#f9fafb' },
                        '&:last-child': { borderBottom: 'none' },
                        transition: 'background-color 0.15s ease',
                        minWidth: '700px',
                      }}>
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>{index + 1}</Typography>

                        <Chip label={role.role_name} size="small"
                          sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '12px', width: 'fit-content' }} />

                        <Typography variant="body2" color="text.secondary">{role.role_desc || 'N/A'}</Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(role.createddt)}
                        </Typography>

                        {/* ✅ Edit & Delete action buttons */}
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => handleOpenDialog(role)} disabled={isDeleting}
                            sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(role)} disabled={isDeleting}
                            sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}>
                            {isDeleting
                              ? <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                              : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <SecurityIcon sx={{ fontSize: 64, color: '#c4cdd5', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No roles found</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {apiError ? 'Unable to load data.' : 'Click "Add Role" to create your first role'}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={loading}
                      sx={{ textTransform: 'none', bgcolor: '#008060', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}>
                      Add Role
                    </Button>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e1e3e5', fontWeight: 600, color: '#202223' }}>
          {editMode ? 'Edit Role' : 'Add New Role'}
          <IconButton size="small" onClick={handleCloseDialog} disabled={isSaving}><CloseIcon /></IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2.5}>
              <TextField fullWidth label="Role Name" name="role_name"
                value={formData.role_name} onChange={handleInputChange}
                error={!!formErrors.role_name}
                helperText={formErrors.role_name || 'Enter role name (e.g., Admin, Manager)'}
                disabled={isSaving} placeholder="Enter role name" required />
              <TextField fullWidth label="Role Description" name="role_desc"
                value={formData.role_desc} onChange={handleInputChange}
                error={!!formErrors.role_desc}
                helperText={formErrors.role_desc || 'Enter a brief description of the role'}
                multiline rows={3} disabled={isSaving} placeholder="Enter role description" required />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #e1e3e5' }}>
            <Button onClick={handleCloseDialog} disabled={isSaving}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336', borderRadius: '8px' }}>
              Close
            </Button>
            <Button onClick={handleCloseDialog} disabled={isSaving}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12', borderRadius: '8px' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving}
              sx={{ textTransform: 'none', bgcolor: '#008060', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}>
              {isSaving
                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    {editMode ? 'Updating...' : 'Adding...'}
                  </Box>
                : editMode ? 'Update Role' : 'Add Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMaster;